import { createRoot } from 'react-dom/client';
import App from './App';
import './App.css';
import './index.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Dashboard from './Components/Dashboard/Dashboard';
import { Provider } from 'react-redux';
import { store } from './Reducers/store';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { fetchCurrentUser, setUserFromToken } from './Actions/index';
import { isAfter, toDate } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import UserProfile from './Components/User/UserProfile';
import EditUserProfile from './Components/User/EditUserProfile';
import UserList from './Components/User/UserList';
import Login from './Components/User/Login';
import RequireAuth from './Components/Auth/RequireAuth';
import ErrorPage from './Components/Common/ErrorPage';
import { DecodedToken } from './Models/DecodedToken';
import ResetPassword from './Components/User/ResetPassword';
import ForgotPassword from './Components/User/ForgotPassword';
import ClientList from './Components/Client/ClientList';
import RegisterUser from './Components/User/RegisterUser';
import ClientProfile from './Components/Client/ClientProfile';
import ClientProfileEdit from './Components/Client/ClientProfileEdit';
import VendorList from './Components/Vendor/VendorList';
import VendorProfile from './Components/Vendor/VendorProfile';
import VendorProfileEdit from './Components/Vendor/VendorProfileEdit';
import InventoryList from './Components/Inventory/InventoryList';
import JobList from './Components/Job/JobList';
import config from 'devextreme/core/config';
import { licenseKey } from './devextreme-license';
import OrderForm from './Components/Order/OrderForm';
import OrderConfirmation from './Components/Order/OrderConfirmation';
import OrderList from './Components/Order/OrderList';
import AssetList from './Components/Asset/AssetList';
import Settings from './Components/Settings/Settings';
import DeliveryCompleteEmail from './Components/Settings/DeliveryCompleteEmail';
import TimesheetAdmin from './Components/Settings/TimesheetAdmin';
import InventoryDailyPriceList from './Components/Inventory/DailyPrice/InventoryDailyPriceList';
import DeliveryList from './Components/Job/Delivery/DeliveryList';
import InspectionList from './Components/Inspection/InspectionList';
import ReimbursementList from './Components/Reimbursement/ReimbursementList';

config({ licenseKey });
const router = createBrowserRouter([
  {
    element: <Login />,
    path: '/Login',
  },
  {
    element: <ForgotPassword />,
    path: '/forgotpassword',
  },
  {
    element: <RegisterUser />,
    path: '/register',
  },
  {
    element: <ResetPassword />,
    path: '/resetpassword/:guid',
  },
  {
    element: <ResetPassword />,
    path: '/resetpassword',
  },
  {
    element: (
      <RequireAuth>
        <App />
      </RequireAuth>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <Dashboard />,
      },
      {
        path: '/clients',
        element: <ClientList />,
      },
      {
        path: '/client/:id',
        element: <ClientProfile />,
      },
      {
        path: '/client/edit/:id',
        element: <ClientProfileEdit />,
      },
      {
        path: '/vendors',
        element: <VendorList />,
      },
      {
        path: '/vendor/:id',
        element: <VendorProfile />,
      },
      {
        path: '/vendor/edit/:id',
        element: <VendorProfileEdit />,
      },
      {
        path: '/users',
        element: <UserList />,
      },
      {
        path: '/user/:id',
        element: <UserProfile />,
      },
      {
        path: '/edit/user/:id',
        element: <EditUserProfile />,
      },
      {
        path: '/inventory/prices',
        element: <InventoryDailyPriceList />,
      },
      {
        path: '/inventory/list',
        element: <InventoryList />,
      },
      {
        path: '/jobs',
        element: <JobList />,
      },
      {
        path: '/delivery/list',
        element: <DeliveryList />,
      },
      {
        path: '/order-fuel',
        element: <OrderForm />,
      },
      {
        path: '/order-success',
        element: <OrderConfirmation />,
      },
      {
        path: '/orders',
        element: <OrderList />,
      },
      {
        path: '/assets',
        element: <AssetList />,
      },
      {
        path: '/inspections',
        element: <InspectionList />,
      },
      {
        path: '/reimbursements',
        element: <ReimbursementList />,
      },
      {
        path: '/admin',
        element: <Settings />,
      },
      {
        path: '/admin/timesheet',
        element: <TimesheetAdmin />,
      },
      {
        path: '/admin/settings/deliverycompleteemail',
        element: <DeliveryCompleteEmail />,
      },
    ],
  },
]);

const token = localStorage.getItem('FuelProToken');
if (token) {
  const decoded: DecodedToken = jwtDecode(token);
  const now = new Date();
  store.dispatch(
    setUserFromToken({
      email:
        decoded[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ],
      exp: decoded.exp,
    })
  );
  const isValid = isAfter(toDate(decoded.exp * 1000), now);
  if (isValid) {
    store.dispatch(fetchCurrentUser());
  }
}

createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    <RouterProvider router={router} />
    <ToastContainer
      position='bottom-center'
      autoClose={2000}
      limit={3}
      closeOnClick
      theme='dark'
    />
  </Provider>
);
